"use client";

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function SimaxDifferenceSection() {
  const comparisonData = [
    {
      feature: 'Transparency',
      simax: 'Every phase has clear deliverables and approvals',
      traditional: 'Black box processes with unclear milestones'
    },
    {
      feature: 'Risk Reduction',
      simax: 'Start small with assessment, scale based on results',
      traditional: 'Large upfront commitment required'
    },
    {
      feature: 'Technology Choice',
      simax: 'Platform-agnostic recommendations based on your needs',
      traditional: 'Locked into vendor partnerships'
    },
    {
      feature: 'Post-Launch Support',
      simax: 'Ongoing optimization and continuous improvement',
      traditional: 'Limited support after implementation'
    }
  ];

  const approachBlocks = [
    {
      title: 'TRANSPARENCY',
      description: 'Every phase has clear deliverables and approvals. You always know what is happening, why it matters, and what comes next.'
    },
    {
      title: 'PHASED RISK REDUCTION',
      description: 'Start small with an assessment. See the value. Then decide whether to continue with full implementation. No massive upfront commitment.'
    },
    {
      title: 'TECHNOLOGY NEUTRALITY',
      description: 'We recommend tools based on your needs, not vendor partnerships. We can work with any major cloud platform or data warehouse technology.'
    },
    {
      title: 'LONG-TERM PARTNERSHIP',
      description: 'We continue to support and improve your data warehouse after implementation so it grows with your business and adapts to changing needs.'
    }
  ];

  return (
    <section className="bg-cream py-12 sm:py-16 md:py-24 px-4 sm:px-6 min-h-0">
      <div className="max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="text-left md:text-center mb-10 sm:mb-16">
          <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] mb-3 sm:mb-4">
            OUR APPROACH
          </p>
          <h2 className="font-serif text-navy text-[26px] sm:text-[36px] md:text-[48px] leading-[1.2] font-bold mb-4 sm:mb-6">
            The Simax Difference
          </h2>
          <p className="text-charcoal text-base sm:text-lg md:text-xl leading-[1.7] max-w-3xl md:mx-auto">
            How we&apos;re different from typical data consulting companies
          </p>
        </div>

        {/* Comparison Table - scroll horizontally on small screens */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-x-auto overflow-y-hidden mb-12 sm:mb-20 -mx-4 sm:mx-0 px-4 sm:px-0">
          <div className="min-w-[560px] sm:min-w-0">
            <div className="grid grid-cols-3 bg-navy text-white">
              <div className="p-3 sm:p-6 border-r border-white/10">
                <h3 className="font-serif text-sm sm:text-xl font-bold">Feature</h3>
              </div>
              <div className="p-3 sm:p-6 border-r border-white/10">
                <h3 className="font-serif text-sm sm:text-xl font-bold">Simax Approach</h3>
              </div>
              <div className="p-3 sm:p-6">
                <h3 className="font-serif text-sm sm:text-xl font-bold">Traditional Vendors</h3>
              </div>
            </div>
            {comparisonData?.map((row, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 ${index !== comparisonData?.length - 1 ? 'border-b border-gray-200' : ''}`}
              >
                <div className="p-3 sm:p-6 border-r border-gray-200">
                  <p className="font-semibold text-navy text-sm sm:text-base">{row?.feature}</p>
                </div>
                <div className="p-3 sm:p-6 border-r border-gray-200 bg-green-50">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-charcoal text-xs sm:text-sm">{row?.simax}</p>
                  </div>
                </div>
                <div className="p-3 sm:p-6 bg-red-50">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-charcoal text-xs sm:text-sm">{row?.traditional}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Our Approach Works */}
        <div className="mb-8 sm:mb-12">
          <h3 className="font-serif text-navy text-xl sm:text-2xl md:text-3xl font-bold text-left md:text-center mb-8 sm:mb-12">
            Why Our Approach Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
            {approachBlocks?.map((block, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-5 sm:p-6 md:p-8 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <h4 className="text-gold text-xs sm:text-sm font-bold uppercase tracking-[1.5px] mb-3 sm:mb-4">
                  {block?.title}
                </h4>
                <p className="text-charcoal text-[14px] sm:text-base leading-[1.7]">
                  {block?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}